import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Radio,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    CardMedia,
    IconButton,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo, useState } from 'react';
import noPreviewImage from '@/assets/images/no-preview.png';
import { useTranslation } from 'react-i18next';
export type LayoutOption = {
    id: number;
    name: string;
    previewUrl: string;
    purpose?: number | null;
};

type Props = {
    open: boolean;
    title?: string;
    eslMac?: string;
    options: LayoutOption[];
    onConfirm: (id: number) => void;
    onClose: () => void;
    onExited?: () => void;
    clear?: () => void;
    maxHeight?: number | string; // 可傳 '50vh'
};

const DefaultLayoutDialog = ({
    open,
    eslMac,
    title,
    options,
    onConfirm,
    onClose,
    onExited,
    clear = () => {},
    maxHeight = '50vh',
}: Props) => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const dialogTitle = useMemo(() => {
        if (title) {
            return title;
        }
        if (eslMac) {
            return `ESL - [ ${eslMac} ]`;
        }
        return t('Default Layout');
    }, [title, eslMac, t]);

    const filteredOptions = useMemo(() => {
        const usedIds = new Set<number>();

        return options.filter((option) => {
            if (usedIds.has(option.id)) return false;

            usedIds.add(option.id);
            return true;
        });
    }, [options]);

    return (
        <>
            <Dialog
                open={open}
                onClose={(_e, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    onClose();
                }}
                fullWidth
                maxWidth="sm"
                TransitionProps={{
                    onExited: () => {
                        clear();
                        setSelectedId(null);
                        if (onExited) {
                            onExited();
                        }
                    },
                }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    {dialogTitle}
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        p: 2, // 不用分 xs / sm，差異體感很低
                    }}
                >
                    <List
                        disablePadding
                        sx={{
                            maxHeight,
                            overflowY: 'auto',
                            borderRadius: 1,
                            border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                        aria-label="default layout options"
                    >
                        {filteredOptions.map((opt) => {
                            const checked = opt.id === selectedId;
                            return (
                                <ListItemButton
                                    key={opt.id}
                                    role="option"
                                    selected={checked}
                                    onClick={() => setSelectedId(opt.id)}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        alignItems: 'stretch',
                                        '&.Mui-selected': { bgcolor: 'action.selected' },
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }} // 這個保留
                                        spacing={2}
                                        sx={{ width: '100%' }}
                                    >
                                        {/* 左側：Radio + Name */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                pr: 2,
                                            }}
                                        >
                                            <Radio
                                                edge="start"
                                                checked={checked}
                                                tabIndex={-1}
                                                inputProps={{ 'aria-label': opt.name }}
                                            />
                                            <ListItemText
                                                primary={opt.name}
                                                primaryTypographyProps={{
                                                    sx: {
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        wordBreak: 'break-word',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                    },
                                                }}
                                            />
                                        </Stack>

                                        {/* 右側：Preview */}
                                        <CardMedia
                                            component="img"
                                            src={opt.previewUrl}
                                            alt={`${opt.name} preview`}
                                            onError={(_event) => {
                                                (_event.currentTarget as HTMLImageElement).src = noPreviewImage;
                                            }}
                                            sx={{
                                                flexShrink: 0,
                                                width: { xs: '100%', sm: 280 }, // 只留這一個 breakpoint
                                                aspectRatio: '250 / 122', // 讓比例正確
                                                borderRadius: 1,
                                                border: (t) => `1px solid ${t.palette.divider}`,
                                                objectFit: 'contain',
                                                alignSelf: { xs: 'stretch', sm: 'center' },
                                            }}
                                        />
                                    </Stack>
                                </ListItemButton>
                            );
                        })}
                    </List>
                </DialogContent>

                <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Button variant="outlined" onClick={onClose}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => selectedId != null && onConfirm(selectedId)}
                        disabled={selectedId == null}
                    >
                        {t('Layout Apply')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DefaultLayoutDialog;
